import { useState, useRef } from "react";
import { Modal } from "./ui/Modal";
import { Button } from "./ui/Button";
import { Download, Upload } from "lucide-react";
import { useStore } from "../state/store";
import { migrateAndValidate } from "../storage/migrate";
import { savePersonalScenario } from "../storage/personalScenarios";
import { randomUUID } from "../utils/uuid";

interface Props {
  open: boolean;
  onClose: () => void;
  mode: "import" | "export";
}

export function ImportExportDialog({ open, onClose, mode }: Props) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={mode === "import" ? "Scenario importeren" : "Scenario exporteren"}
      width="32rem"
    >
      {mode === "import" ? <ImportSection onClose={onClose} /> : <ExportSection />}
    </Modal>
  );
}

function ExportSection() {
  const scenario = useStore((s) => s.scenario);
  const json = JSON.stringify(scenario, null, 2);

  function download() {
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const safe = scenario.name.replace(/[^a-z0-9-_]+/gi, "-").toLowerCase() || "scenario";
    a.href = url;
    a.download = `schaduwtool-${safe}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function copyToClipboard() {
    navigator.clipboard.writeText(json).catch(() => {
      // klembord-permissie ontbreekt — gebruiker kan handmatig kopiëren.
    });
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Het volledige scenario inclusief blokken, hoogtes en kaartzicht wordt
        als JSON ge-exporteerd. Schemaversie {scenario.schemaVersion}.
      </p>
      <textarea
        value={json}
        readOnly
        className="w-full font-mono text-xs bg-secondary border border-input rounded-md p-2 h-48"
      />
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={copyToClipboard}>
          Kopieer JSON
        </Button>
        <Button size="sm" onClick={download}>
          <Download size={14} /> Download bestand
        </Button>
      </div>
    </div>
  );
}

function ImportSection({ onClose }: { onClose: () => void }) {
  const loadScenario = useStore((s) => s.loadScenario);
  const refreshPersonalList = useStore((s) => s.refreshPersonalList);
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [drag, setDrag] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  async function importJson(rawJson: string) {
    setError(null);
    let parsed: unknown;
    try {
      parsed = JSON.parse(rawJson);
    } catch (err) {
      setError("Geen geldig JSON: " + (err instanceof Error ? err.message : String(err)));
      return;
    }
    const result = migrateAndValidate(parsed);
    if (!result.ok || !result.scenario) {
      setError(result.error ?? "Onbekende validatiefout.");
      return;
    }
    // Importeer als persoonlijke kopie — geef een nieuw id zodat het niet
    // botst met een bestaand publiek scenario.
    const personal = {
      ...result.scenario,
      id: randomUUID(),
      isPublic: false,
      updatedAt: new Date().toISOString(),
    };
    await savePersonalScenario(personal);
    await refreshPersonalList();
    loadScenario(personal);
    onClose();
  }

  function onFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    const f = files[0];
    f.text().then(importJson);
  }

  return (
    <div className="space-y-3">
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDrag(false);
          onFiles(e.dataTransfer.files);
        }}
        className={
          "border-2 border-dashed rounded-md p-6 text-center text-sm transition-colors " +
          (drag ? "border-blue-500 bg-blue-50" : "border-input bg-background")
        }
      >
        Sleep een JSON-bestand hierheen of{" "}
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="underline text-blue-700"
        >
          kies een bestand
        </button>
        .
        <input
          ref={fileRef}
          type="file"
          accept="application/json"
          className="hidden"
          onChange={(e) => onFiles(e.target.files)}
        />
      </div>

      <div>
        <label className="block text-xs font-medium mb-1">Of plak JSON</label>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="{ ... }"
          className="w-full font-mono text-xs bg-background border border-input rounded-md p-2 h-32"
        />
      </div>

      {error && (
        <div className="text-xs text-destructive bg-rose-50 border border-rose-200 rounded-md p-2">
          {error}
        </div>
      )}

      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" size="sm" onClick={onClose}>
          Annuleer
        </Button>
        <Button
          size="sm"
          onClick={() => importJson(text)}
          disabled={text.trim().length === 0}
        >
          <Upload size={14} /> Importeren als persoonlijk scenario
        </Button>
      </div>
    </div>
  );
}
